import styled from '@emotion/styled'
import { EMPTY_PREBID_MODULE, PrebidModule, PrebidModuleCatalogEntry, PrebidModuleDataBySite, PrebidModuleType } from '@pubstack/common/src/prebidModule'
import { byLabel } from '@pubstack/common/src/sort'
import { FunctionComponent } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import Button from '~/components/Button'
import { CollapsiblePanel } from '~/components/CollapsiblePanel'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { Input } from '~/components/Input'
import { Select } from '~/components/Select'
import { Skeleton } from '~/components/Skeleton'
import { handleTableSearch } from '~/components/table/Table'
import { WithClassName } from '~/types/utils'
import { CollapsiblePrebidModule } from '../modules/CollapsiblePrebidModule'

const ModulesWrapper = styled.div``

const ModulePanel = styled(CollapsiblePanel)`
  margin-bottom: 20px;
`
const SearchBar = styled.form`
  margin-bottom: 12px;
  display: flex;
  flex-grow: 1;
  flex-shrink: 0;
  align-items: center;
  gap: 12px;
  ${Button} {
    margin-top: 4px;
  }
`

const SitesModule = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

type PureModuleBySiteProps = WithClassName & {
  isLoading: boolean
  modules: PrebidModuleDataBySite<PrebidModuleType>[]
  catalog: PrebidModuleCatalogEntry[]
  updateSiteData: (data: PrebidModuleDataBySite<PrebidModuleType>) => Promise<void>
}

export const PureModulesBySite: FunctionComponent<PureModuleBySiteProps> = ({ isLoading, modules, catalog, updateSiteData }) => {
  const sortedModules = [...modules].sort(byLabel)
  const {
    resetField,
    control,
    formState: { isDirty },
  } = useForm<{ search: string; status: string }>({
    defaultValues: {
      search: '',
      status: 'allModules',
    },
  })
  const { search, status } = useWatch({ control })
  const searchedModules = handleTableSearch(sortedModules, search ?? '', ['label'])
  const filteredModules = searchedModules.filter((a) => (status === 'usedModules' ? a.enabled : status === 'notUsedModules' ? !a.enabled : true))

  const getPrebidModule = (moduleLabel: string): PrebidModule => {
    return catalog.find((module) => module.displayName === moduleLabel) ?? EMPTY_PREBID_MODULE
  }

  return (
    <ModulesWrapper>
      <SearchBar>
        <Input name={'search'} type={'text'} iconLeft={'search'} labelIsPlaceholder label={'Search'} control={control} />
        <Select
          control={control}
          name={'status'}
          options={[
            { label: 'All modules', value: 'allModules', trackingLabel: 'adm-sites-stacks-modules-no-filter-apply' },
            { label: 'Used', value: 'usedModules', trackingLabel: 'adm-sites-stacks-modules-used-filter-apply' },
            { label: 'Not used', value: 'notUsedModules', trackingLabel: 'adm-sites-stacks-modules-unused-filter-apply' },
          ]}
          label={'Status'}
        />

        {isDirty && (
          <Button
            iconName={'close'}
            variant={'tertiary'}
            onClick={() => {
              resetField('search')
              resetField('status')
            }}
            trackingLabel={'adm-sites-stacks-modules-filter-restore'}
          />
        )}
      </SearchBar>
      {!isLoading ? (
        filteredModules.length > 0 ? (
          <SitesModule>
            {filteredModules.map((module) => (
              <CollapsiblePrebidModule key={module.label} data={module} isLoading={isLoading} updateSiteData={updateSiteData} prebidModule={getPrebidModule(module.label)} />
            ))}
          </SitesModule>
        ) : (
          <IllustrationMessage iconName={'night_sky'} iconSize={'190px'} title={'No modules.'} />
        )
      ) : (
        [0, 1, 2].map((e) => <ModulePanel key={e} forceState={'closed'} hasBorderWhenOpened title={<Skeleton width={`15ch`} />} />)
      )}
    </ModulesWrapper>
  )
}
